import { useCallback, useEffect, useMemo, useState } from 'react'

import { useOidc } from '@axa-fr/react-oidc-context'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import {
  LegalDocumentType,
  UnacceptedLegalDocumentDto,
  useAcceptLegalDocumentMutation,
  useGetDriverUnacceptedLegalDocumentsQuery,
} from 'generated/graphql'

import { DOCUMENT_TYPE_LABELS } from '../../settings/LegalDocuments/constants'

const LINK_COLOR = '#4f8afb'

const IDENTITY_URL = process.env.REACT_APP_AUTH_URL || ''

const DOCUMENT_TYPE_PATH: Record<string, string> = {
  [LegalDocumentType.TermsAndConditions]: '/driver/terms',
  [LegalDocumentType.PrivacyPolicy]: '/driver/privacy-policy',
  [LegalDocumentType.CommercialTerms]: '/driver/commercial-terms',
}

const makeDocLink = (doc: UnacceptedLegalDocumentDto) => (
  <Link
    href={`${IDENTITY_URL}${DOCUMENT_TYPE_PATH[doc.documentType] ?? ''}`}
    target='_blank'
    rel='noopener noreferrer'
    underline='always'
    sx={{ color: LINK_COLOR, textDecorationColor: LINK_COLOR }}>
    {DOCUMENT_TYPE_LABELS[doc.documentType] ?? doc.name}
  </Link>
)

export default function LegalDocumentAcceptanceDialog() {
  const [isOpen, setIsOpen] = useState(false)
  const [acceptedDocIds, setAcceptedDocIds] = useState<Set<number>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { isAuthenticated, logout } = useOidc()

  const {
    data: unacceptedData,
    loading: unacceptedLoading,
    refetch,
  } = useGetDriverUnacceptedLegalDocumentsQuery({
    fetchPolicy: 'network-only',
  })

  const allUnacceptedDocuments: UnacceptedLegalDocumentDto[] = useMemo(() => {
    if (!unacceptedData?.driverUnacceptedLegalDocuments) return []
    const { majorUnacceptedDocuments, minorUnacceptedDocuments, patchUnacceptedDocuments } =
      unacceptedData.driverUnacceptedLegalDocuments
    return [
      ...(majorUnacceptedDocuments ?? []),
      ...(minorUnacceptedDocuments ?? []),
      ...(patchUnacceptedDocuments ?? []),
    ]
  }, [unacceptedData])

  const blockingDocuments = useMemo(
    () =>
      allUnacceptedDocuments.filter(
        (d) =>
          d.documentType === LegalDocumentType.CommercialTerms ||
          d.documentType === LegalDocumentType.PrivacyPolicy,
      ),
    [allUnacceptedDocuments],
  )

  useEffect(() => {
    if (!unacceptedLoading && blockingDocuments.length > 0) {
      setIsOpen(true)
    }
  }, [unacceptedLoading, blockingDocuments])

  const [acceptLegalDocument] = useAcceptLegalDocumentMutation()

  const handleCheckboxChange = useCallback((docId: number, checked: boolean) => {
    setAcceptedDocIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(docId)
      } else {
        next.delete(docId)
      }
      return next
    })
  }, [])

  const allChecked =
    blockingDocuments.length > 0 && blockingDocuments.every((d) => acceptedDocIds.has(Number(d.id)))

  const handleCancel = () => {
    logout(`${process.env.REACT_APP_PUBLIC_URL}`)
  }

  const handleAccept = async () => {
    if (!allChecked) return

    setIsSubmitting(true)
    try {
      for (const doc of blockingDocuments) {
        await acceptLegalDocument({
          variables: {
            legalDocumentId: Number(doc.id),
          },
        })
      }

      toast.success('Legal documents accepted successfully.')
      setIsOpen(false)
      setAcceptedDocIds(new Set())
      refetch()
    } catch (error) {
      toast.error('Failed to accept legal documents. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isAuthenticated || unacceptedLoading) {
    return null
  }

  if (blockingDocuments.length === 0) {
    return null
  }

  const docLinks = blockingDocuments.map((doc, i) => (
    <span key={doc.id}>
      {i > 0 && ' and '}
      {makeDocLink(doc)}
    </span>
  ))

  return (
    <Dialog
      open={isOpen}
      maxWidth='xs'
      fullWidth
      disableEscapeKeyDown
      aria-label='legal-document-acceptance-dialog'
      PaperProps={{
        sx: {
          borderRadius: 3,
          '& .MuiDialogTitle-root .MuiIconButton-root': { transform: 'scale(1.07)' },
        },
      }}>
      <FplDialogTitle id='legal-document-acceptance-dialog' onClose={handleCancel}>
        Legal Documents Required
      </FplDialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant='body2' sx={{ mb: 2.5, mt: 1 }}>
          Before you proceed, please take a moment to review our {docLinks}. By clicking
          &quot;Proceed&quot;, you agree to be bound by these terms.
        </Typography>

        {blockingDocuments.map((doc) => (
          <FormControlLabel
            key={doc.id}
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
            control={
              <Checkbox
                checked={acceptedDocIds.has(Number(doc.id))}
                onChange={(e) => handleCheckboxChange(Number(doc.id), e.target.checked)}
                disabled={isSubmitting}
                icon={<CheckBoxOutlineBlankIcon sx={{ borderRadius: '4px' }} />}
                checkedIcon={<CheckBoxIcon sx={{ borderRadius: '4px' }} />}
                sx={{ borderRadius: '4px' }}
              />
            }
            label={
              <Typography variant='body2'>
                I have read and agree to the {makeDocLink(doc)}.
              </Typography>
            }
          />
        ))}
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 3, pb: 2.5 }}>
        <Button
          variant='contained'
          onClick={handleAccept}
          disabled={!allChecked || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={18} color='inherit' /> : undefined}
          sx={{
            minWidth: 120,
            borderRadius: 6,
            color: '#fff',
            ...(!allChecked || isSubmitting
              ? {
                  backgroundColor: '#bdbdbd',
                  borderColor: '#bdbdbd',
                  '&.Mui-disabled': { backgroundColor: '#bdbdbd', color: '#fff' },
                }
              : {
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }),
          }}>
          {isSubmitting ? 'Accepting...' : 'Proceed'}
        </Button>
        <Button
          variant='outlined'
          onClick={handleCancel}
          disabled={isSubmitting}
          sx={{
            minWidth: 120,
            borderRadius: 6,
            color: LINK_COLOR,
            borderColor: LINK_COLOR,
            '&:hover': { borderColor: LINK_COLOR, backgroundColor: 'rgba(79,138,251,0.08)' },
          }}>
          Cancel
        </Button>
      </Box>
    </Dialog>
  )
}
